import React, { useRef, useState, useEffect } from 'react';
import styles from './Svg3D.module.css';

const Svg3DWrapper = ({ children, position, size = 140, opacity = 0.6, className, float = true, viewBox = '0 0 200 200' }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`${styles.wrapper} ${isVisible ? styles.visible : ''} ${float ? styles.float : ''} ${className || ''}`}
      style={{
        position: 'absolute',
        pointerEvents: 'none',
        zIndex: 0,
        width: size,
        height: size,
        opacity: isVisible ? opacity : 0,
        ...position,
      }}
    >
      <svg viewBox={viewBox} fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
        {children}
      </svg>
    </div>
  );
};

/* ===== 3D SVG Library ===== */

// 3D Isometric Cube
export const IsoCube = (props) => (
  <Svg3DWrapper {...props} viewBox="0 0 200 200">
    <defs>
      <linearGradient id="cubeTop" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#a2de21" stopOpacity="1" />
        <stop offset="100%" stopColor="#7ab818" stopOpacity="0.95" />
      </linearGradient>
      <linearGradient id="cubeLeft" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#3B46D0" stopOpacity="1" />
        <stop offset="100%" stopColor="#2a33c0" stopOpacity="0.9" />
      </linearGradient>
      <linearGradient id="cubeRight" x1="1" y1="0" x2="0" y2="0">
        <stop offset="0%" stopColor="#5058f7" stopOpacity="0.95" />
        <stop offset="100%" stopColor="#3B46D0" stopOpacity="0.85" />
      </linearGradient>
    </defs>
    {/* Top face */}
    <polygon points="100,30 160,65 100,100 40,65" fill="url(#cubeTop)" />
    {/* Left face */}
    <polygon points="40,65 100,100 100,160 40,125" fill="url(#cubeLeft)" />
    {/* Right face */}
    <polygon points="160,65 100,100 100,160 160,125" fill="url(#cubeRight)" />
    {/* Edges */}
    <polygon points="100,30 160,65 100,100 40,65" stroke="rgba(255,255,255,0.5)" strokeWidth="1" fill="none" />
    <polygon points="40,65 100,100 100,160 40,125" stroke="rgba(255,255,255,0.4)" strokeWidth="1" fill="none" />
    <polygon points="160,65 100,100 100,160 160,125" stroke="rgba(255,255,255,0.4)" strokeWidth="1" fill="none" />
  </Svg3DWrapper>
);

// 3D Floating Sphere with rings
export const FloatingSphere = (props) => (
  <Svg3DWrapper {...props} viewBox="0 0 200 200">
    <defs>
      <radialGradient id="sphereGrad" cx="40%" cy="35%" r="50%">
        <stop offset="0%" stopColor="#a2de21" stopOpacity="1" />
        <stop offset="60%" stopColor="#3B46D0" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#1a1f80" stopOpacity="0.75" />
      </radialGradient>
      <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#a2de21" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#3B46D0" stopOpacity="0.75" />
      </linearGradient>
    </defs>
    {/* Shadow */}
    <ellipse cx="100" cy="170" rx="50" ry="12" fill="rgba(0,0,0,0.15)" />
    {/* Sphere */}
    <circle cx="100" cy="90" r="50" fill="url(#sphereGrad)" />
    {/* Highlight */}
    <ellipse cx="82" cy="72" rx="18" ry="14" fill="rgba(255,255,255,0.25)" />
    {/* Orbit ring */}
    <ellipse cx="100" cy="90" rx="70" ry="20" stroke="url(#ringGrad)" strokeWidth="2" fill="none" transform="rotate(-15 100 90)" />
    {/* Orbit dot */}
    <circle cx="168" cy="85" r="5" fill="#a2de21" opacity="0.8" />
  </Svg3DWrapper>
);

// 3D Isometric Server/Database Stack
export const IsoServer = (props) => (
  <Svg3DWrapper {...props} viewBox="0 0 200 220">
    <defs>
      <linearGradient id="serverTop" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#a2de21" stopOpacity="1" />
        <stop offset="100%" stopColor="#7ab818" stopOpacity="0.9" />
      </linearGradient>
      <linearGradient id="serverFront" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#3B46D0" stopOpacity="0.95" />
        <stop offset="100%" stopColor="#2a33c0" stopOpacity="0.85" />
      </linearGradient>
      <linearGradient id="serverSide" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#5058f7" stopOpacity="0.85" />
        <stop offset="100%" stopColor="#3B46D0" stopOpacity="0.75" />
      </linearGradient>
    </defs>
    {/* Bottom server unit */}
    <polygon points="100,120 155,150 100,180 45,150" fill="url(#serverTop)" />
    <polygon points="45,150 100,180 100,200 45,170" fill="url(#serverFront)" />
    <polygon points="155,150 100,180 100,200 155,170" fill="url(#serverSide)" />
    {/* Middle server unit */}
    <polygon points="100,80 155,110 100,140 45,110" fill="url(#serverTop)" opacity="0.9" />
    <polygon points="45,110 100,140 100,160 45,130" fill="url(#serverFront)" />
    <polygon points="155,110 100,140 100,160 155,130" fill="url(#serverSide)" />
    {/* Top server unit */}
    <polygon points="100,40 155,70 100,100 45,70" fill="url(#serverTop)" />
    <polygon points="45,70 100,100 100,120 45,90" fill="url(#serverFront)" />
    <polygon points="155,70 100,100 100,120 155,90" fill="url(#serverSide)" />
    {/* Status LEDs */}
    <circle cx="62" cy="85" r="3" fill="#a2de21" opacity="0.9" />
    <circle cx="72" cy="82" r="3" fill="#a2de21" opacity="0.6" />
    <circle cx="62" cy="125" r="3" fill="#a2de21" opacity="0.9" />
    <circle cx="72" cy="122" r="3" fill="#a2de21" opacity="0.6" />
    <circle cx="62" cy="165" r="3" fill="#a2de21" opacity="0.5" />
    {/* Edges */}
    <polygon points="100,40 155,70 100,100 45,70" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" fill="none" />
    <polygon points="100,80 155,110 100,140 45,110" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" fill="none" />
    <polygon points="100,120 155,150 100,180 45,150" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" fill="none" />
  </Svg3DWrapper>
);

// 3D Shield with checkmark
export const IsoShield = (props) => (
  <Svg3DWrapper {...props} viewBox="0 0 200 220">
    <defs>
      <linearGradient id="shieldFill" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#3B46D0" stopOpacity="1" />
        <stop offset="100%" stopColor="#1a1f80" stopOpacity="0.85" />
      </linearGradient>
      <linearGradient id="shieldGlow" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#a2de21" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#a2de21" stopOpacity="0.1" />
      </linearGradient>
    </defs>
    {/* Shadow */}
    <ellipse cx="100" cy="195" rx="45" ry="10" fill="rgba(0,0,0,0.12)" />
    {/* Shield body */}
    <path d="M100,25 L155,55 L155,115 Q155,160 100,190 Q45,160 45,115 L45,55 Z" fill="url(#shieldFill)" />
    {/* Shield shine */}
    <path d="M100,25 L155,55 L155,115 Q155,160 100,190 Q45,160 45,115 L45,55 Z" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" fill="none" />
    {/* Inner shield */}
    <path d="M100,45 L140,68 L140,112 Q140,148 100,172 Q60,148 60,112 L60,68 Z" stroke="url(#shieldGlow)" strokeWidth="1.5" fill="none" />
    {/* Checkmark */}
    <polyline points="78,105 93,122 125,82" stroke="#a2de21" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </Svg3DWrapper>
);

// 3D Floating Diamond/Gem
export const FloatingDiamond = (props) => (
  <Svg3DWrapper {...props} viewBox="0 0 200 200">
    <defs>
      <linearGradient id="diamondTop" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#a2de21" stopOpacity="1" />
        <stop offset="100%" stopColor="#7ab818" stopOpacity="0.9" />
      </linearGradient>
      <linearGradient id="diamondLeft" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#3B46D0" stopOpacity="1" />
        <stop offset="100%" stopColor="#1a1f80" stopOpacity="0.9" />
      </linearGradient>
      <linearGradient id="diamondRight" x1="1" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#5058f7" stopOpacity="0.95" />
        <stop offset="100%" stopColor="#3B46D0" stopOpacity="0.85" />
      </linearGradient>
    </defs>
    {/* Shadow */}
    <ellipse cx="100" cy="175" rx="30" ry="8" fill="rgba(0,0,0,0.12)" />
    {/* Top facets */}
    <polygon points="100,20 145,70 100,85" fill="url(#diamondTop)" />
    <polygon points="100,20 55,70 100,85" fill="url(#diamondTop)" opacity="0.7" />
    {/* Bottom facets */}
    <polygon points="55,70 100,85 100,160" fill="url(#diamondLeft)" />
    <polygon points="145,70 100,85 100,160" fill="url(#diamondRight)" />
    {/* Crown line */}
    <line x1="55" y1="70" x2="145" y2="70" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />
    {/* Edges */}
    <polyline points="55,70 100,20 145,70 100,160 55,70" stroke="rgba(255,255,255,0.5)" strokeWidth="1" fill="none" />
    <line x1="100" y1="20" x2="100" y2="160" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" />
    {/* Sparkle */}
    <circle cx="85" cy="55" r="3" fill="rgba(255,255,255,0.6)" />
  </Svg3DWrapper>
);

// 3D Rocket
export const IsoRocket = (props) => (
  <Svg3DWrapper {...props} viewBox="0 0 200 240">
    <defs>
      <linearGradient id="rocketBody" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#e8e8e8" stopOpacity="1" />
        <stop offset="50%" stopColor="#ffffff" stopOpacity="0.95" />
        <stop offset="100%" stopColor="#cccccc" stopOpacity="1" />
      </linearGradient>
      <linearGradient id="rocketNose" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#a2de21" stopOpacity="1" />
        <stop offset="100%" stopColor="#7ab818" stopOpacity="0.95" />
      </linearGradient>
      <linearGradient id="flame" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ff6b35" stopOpacity="1" />
        <stop offset="50%" stopColor="#ffd700" stopOpacity="1" />
        <stop offset="100%" stopColor="#ff4444" stopOpacity="0" />
      </linearGradient>
    </defs>
    {/* Flame */}
    <path d="M88,170 Q100,220 112,170" fill="url(#flame)" />
    <path d="M92,170 Q100,200 108,170" fill="#ffd700" opacity="0.6" />
    {/* Body */}
    <rect x="82" y="80" width="36" height="90" rx="4" fill="url(#rocketBody)" />
    {/* Nose cone */}
    <path d="M82,80 Q82,40 100,20 Q118,40 118,80 Z" fill="url(#rocketNose)" />
    {/* Window */}
    <circle cx="100" cy="105" r="12" fill="#3B46D0" opacity="0.7" />
    <circle cx="100" cy="105" r="9" fill="#1a1f80" opacity="0.5" />
    <ellipse cx="96" cy="101" rx="4" ry="3" fill="rgba(255,255,255,0.4)" />
    {/* Fins */}
    <path d="M82,145 L60,175 L82,170 Z" fill="#3B46D0" opacity="0.7" />
    <path d="M118,145 L140,175 L118,170 Z" fill="#3B46D0" opacity="0.5" />
    {/* Body stripe */}
    <rect x="82" y="130" width="36" height="5" fill="#a2de21" opacity="0.6" />
    {/* Edge highlights */}
    <path d="M82,80 Q82,40 100,20 Q118,40 118,80" stroke="rgba(255,255,255,0.5)" strokeWidth="1" fill="none" />
  </Svg3DWrapper>
);

// 3D Hexagonal prism (data/blockchain feel)
export const HexPrism = (props) => (
  <Svg3DWrapper {...props} viewBox="0 0 200 200">
    <defs>
      <linearGradient id="hexTop" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#a2de21" stopOpacity="1" />
        <stop offset="100%" stopColor="#7ab818" stopOpacity="0.85" />
      </linearGradient>
      <linearGradient id="hexFront" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#3B46D0" stopOpacity="0.95" />
        <stop offset="100%" stopColor="#2a33c0" stopOpacity="0.8" />
      </linearGradient>
      <linearGradient id="hexSide" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#5058f7" stopOpacity="0.85" />
        <stop offset="100%" stopColor="#3B46D0" stopOpacity="0.75" />
      </linearGradient>
    </defs>
    {/* Top face (hexagon) */}
    <polygon points="100,30 145,50 145,90 100,110 55,90 55,50" fill="url(#hexTop)" />
    {/* Front face */}
    <polygon points="55,90 100,110 100,155 55,135" fill="url(#hexFront)" />
    <polygon points="100,110 145,90 145,135 100,155" fill="url(#hexSide)" />
    {/* Bottom front edge */}
    <polygon points="55,50 55,90 55,135 55,95" fill="url(#hexFront)" opacity="0.3" />
    {/* Edges */}
    <polygon points="100,30 145,50 145,90 100,110 55,90 55,50" stroke="rgba(255,255,255,0.5)" strokeWidth="1" fill="none" />
    <line x1="55" y1="90" x2="55" y2="135" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />
    <line x1="100" y1="110" x2="100" y2="155" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />
    <line x1="145" y1="90" x2="145" y2="135" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />
    <line x1="55" y1="135" x2="100" y2="155" stroke="rgba(255,255,255,0.35)" strokeWidth="1" />
    <line x1="145" y1="135" x2="100" y2="155" stroke="rgba(255,255,255,0.35)" strokeWidth="1" />
    {/* Inner detail */}
    <polygon points="100,50 122,62 122,85 100,97 78,85 78,62" stroke="#a2de21" strokeWidth="1" fill="none" opacity="0.4" />
  </Svg3DWrapper>
);

// 3D Chart/Bar graph
export const IsoChart = (props) => (
  <Svg3DWrapper {...props} viewBox="0 0 200 200">
    <defs>
      <linearGradient id="bar1Top" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#a2de21" stopOpacity="1" />
        <stop offset="100%" stopColor="#7ab818" stopOpacity="0.95" />
      </linearGradient>
      <linearGradient id="bar1Front" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#3B46D0" stopOpacity="1" />
        <stop offset="100%" stopColor="#2a33c0" stopOpacity="0.85" />
      </linearGradient>
      <linearGradient id="bar1Side" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#5058f7" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#3B46D0" stopOpacity="0.8" />
      </linearGradient>
    </defs>
    {/* Base platform */}
    <polygon points="30,155 100,190 170,155 100,120" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" />
    {/* Bar 1 (shortest) */}
    <polygon points="45,130 65,140 65,155 45,145" fill="url(#bar1Front)" />
    <polygon points="65,140 80,133 80,148 65,155" fill="url(#bar1Side)" />
    <polygon points="45,130 65,140 80,133 60,123" fill="url(#bar1Top)" />
    {/* Bar 2 (medium) */}
    <polygon points="70,100 90,110 90,155 70,145" fill="url(#bar1Front)" opacity="0.9" />
    <polygon points="90,110 105,103 105,148 90,155" fill="url(#bar1Side)" opacity="0.9" />
    <polygon points="70,100 90,110 105,103 85,93" fill="url(#bar1Top)" />
    {/* Bar 3 (tallest) */}
    <polygon points="95,65 115,75 115,155 95,145" fill="url(#bar1Front)" />
    <polygon points="115,75 130,68 130,148 115,155" fill="url(#bar1Side)" />
    <polygon points="95,65 115,75 130,68 110,58" fill="url(#bar1Top)" />
    {/* Bar 4 (medium-tall) */}
    <polygon points="120,82 140,92 140,155 120,145" fill="url(#bar1Front)" opacity="0.85" />
    <polygon points="140,92 155,85 155,148 140,155" fill="url(#bar1Side)" opacity="0.85" />
    <polygon points="120,82 140,92 155,85 135,75" fill="url(#bar1Top)" opacity="0.9" />
    {/* Trend line */}
    <polyline points="52,125 77,95 110,58 137,77" stroke="#a2de21" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.8" />
    <circle cx="110" cy="58" r="4" fill="#a2de21" opacity="0.9" />
  </Svg3DWrapper>
);

// 3D Connected Nodes / Network
export const IsoNetwork = (props) => (
  <Svg3DWrapper {...props} viewBox="0 0 200 200">
    <defs>
      <radialGradient id="nodeGrad" cx="40%" cy="35%" r="50%">
        <stop offset="0%" stopColor="#a2de21" stopOpacity="1" />
        <stop offset="100%" stopColor="#3B46D0" stopOpacity="0.85" />
      </radialGradient>
    </defs>
    {/* Connections */}
    <line x1="100" y1="50" x2="45" y2="100" stroke="#a2de21" strokeWidth="1.5" opacity="0.4" />
    <line x1="100" y1="50" x2="155" y2="90" stroke="#a2de21" strokeWidth="1.5" opacity="0.4" />
    <line x1="100" y1="50" x2="100" y2="130" stroke="#3B46D0" strokeWidth="1.5" opacity="0.3" />
    <line x1="45" y1="100" x2="100" y2="130" stroke="#3B46D0" strokeWidth="1.5" opacity="0.3" />
    <line x1="155" y1="90" x2="100" y2="130" stroke="#3B46D0" strokeWidth="1.5" opacity="0.3" />
    <line x1="45" y1="100" x2="30" y2="155" stroke="#a2de21" strokeWidth="1" opacity="0.25" />
    <line x1="155" y1="90" x2="170" y2="145" stroke="#a2de21" strokeWidth="1" opacity="0.25" />
    <line x1="100" y1="130" x2="70" y2="170" stroke="#3B46D0" strokeWidth="1" opacity="0.2" />
    <line x1="100" y1="130" x2="135" y2="165" stroke="#3B46D0" strokeWidth="1" opacity="0.2" />
    {/* Main nodes */}
    <circle cx="100" cy="50" r="16" fill="url(#nodeGrad)" />
    <circle cx="100" cy="50" r="16" stroke="rgba(255,255,255,0.5)" strokeWidth="1" fill="none" />
    <circle cx="45" cy="100" r="13" fill="url(#nodeGrad)" opacity="0.8" />
    <circle cx="45" cy="100" r="13" stroke="rgba(255,255,255,0.4)" strokeWidth="1" fill="none" />
    <circle cx="155" cy="90" r="13" fill="url(#nodeGrad)" opacity="0.8" />
    <circle cx="155" cy="90" r="13" stroke="rgba(255,255,255,0.4)" strokeWidth="1" fill="none" />
    <circle cx="100" cy="130" r="14" fill="url(#nodeGrad)" opacity="0.9" />
    <circle cx="100" cy="130" r="14" stroke="rgba(255,255,255,0.25)" strokeWidth="1" fill="none" />
    {/* Secondary nodes */}
    <circle cx="30" cy="155" r="8" fill="#3B46D0" opacity="0.5" />
    <circle cx="170" cy="145" r="8" fill="#3B46D0" opacity="0.5" />
    <circle cx="70" cy="170" r="7" fill="#3B46D0" opacity="0.4" />
    <circle cx="135" cy="165" r="7" fill="#3B46D0" opacity="0.4" />
    {/* Center highlights */}
    <circle cx="95" cy="45" r="5" fill="rgba(255,255,255,0.35)" />
  </Svg3DWrapper>
);

// 3D Gear/Cog
export const IsoCog = (props) => (
  <Svg3DWrapper {...props} viewBox="0 0 200 200">
    <defs>
      <linearGradient id="cogGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#a2de21" stopOpacity="0.95" />
        <stop offset="100%" stopColor="#3B46D0" stopOpacity="0.85" />
      </linearGradient>
    </defs>
    {/* 3D depth shadow */}
    <path d="M100,108 L82,120 L68,118 L58,108 L56,92 L62,80 L58,72 L64,58 L78,54 L88,58 L100,52 L112,58 L122,54 L136,58 L142,72 L138,80 L144,92 L142,108 L132,118 L118,120 Z"
      fill="rgba(0,0,0,0.15)" />
    {/* Main gear */}
    <path d="M100,100 L82,112 L68,110 L58,100 L56,84 L62,72 L58,64 L64,50 L78,46 L88,50 L100,44 L112,50 L122,46 L136,50 L142,64 L138,72 L144,84 L142,100 L132,110 L118,112 Z"
      fill="url(#cogGrad)" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" />
    {/* Inner ring */}
    <circle cx="100" cy="78" r="22" fill="rgba(0,0,0,0.2)" />
    <circle cx="100" cy="78" r="18" fill="#3B46D0" opacity="0.4" />
    <circle cx="100" cy="78" r="18" stroke="rgba(255,255,255,0.4)" strokeWidth="1" fill="none" />
    {/* Center bolt */}
    <circle cx="100" cy="78" r="6" fill="#a2de21" opacity="0.7" />
    {/* Highlight */}
    <ellipse cx="88" cy="65" rx="10" ry="8" fill="rgba(255,255,255,0.15)" />
  </Svg3DWrapper>
);

// 3D Lightning bolt
export const IsoBolt = (props) => (
  <Svg3DWrapper {...props} viewBox="0 0 200 240">
    <defs>
      <linearGradient id="boltGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#a2de21" stopOpacity="0.95" />
        <stop offset="100%" stopColor="#7ab818" stopOpacity="0.95" />
      </linearGradient>
      <linearGradient id="boltShadow" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#3B46D0" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#1a1f80" stopOpacity="0.75" />
      </linearGradient>
    </defs>
    {/* Back shadow/depth */}
    <polygon points="115,25 65,110 95,110 75,200 145,100 110,100 135,25" fill="url(#boltShadow)" transform="translate(4,4)" />
    {/* Main bolt */}
    <polygon points="115,25 65,110 95,110 75,200 145,100 110,100 135,25" fill="url(#boltGrad)" />
    {/* Edge highlight */}
    <polygon points="115,25 65,110 95,110 75,200 145,100 110,100 135,25" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinejoin="round" fill="none" />
    {/* Inner shine */}
    <polygon points="118,40 80,105 100,105 85,175 132,105 112,105 128,40" fill="rgba(255,255,255,0.15)" />
    {/* Glow dots */}
    <circle cx="108" cy="50" r="3" fill="rgba(255,255,255,0.5)" />
  </Svg3DWrapper>
);

export default Svg3DWrapper;
